import '../styles/CustomColorPickerInput.css';

import { type ChangeEvent, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { type IColor, useColor } from 'react-color-palette';

import { ColorPalettePicker } from '@air/component-color-palette-picker';
import { Close as CloseIcon } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { hexToRgb, rgbToHsv } from '@air/utils-color';
import { reportErrorToBugsnag } from '@air/utils-error';

import { sanitizeColorValues } from './utils';

const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}){1,2}$/;
const HIDDEN_INPUT_TYPES: (keyof IColor)[] = ['rgb', 'hsv', 'hex'];

export type CustomColorPickerProps = {
  defaultColor?: string;
  onChange?: (color: string) => void;
  onSave: (color: string) => void;
};

export const CustomColorPickerInput = memo(({ defaultColor = '#FFFFFF', onChange, onSave }: CustomColorPickerProps) => {
  const [color, setColor] = useColor(defaultColor);
  const [touched, setTouched] = useState(false);
  const [hexInput, setHexInput] = useState(color.hex);

  const showClearButton = useMemo(() => {
    return touched && hexInput !== defaultColor;
  }, [touched, hexInput, defaultColor]);

  const handleSave = useCallback(() => {
    onSave(color.hex);
  }, [color.hex, onSave]);

  const handleHexChange = useCallback(
    (hex: string) => {
      const newHex = hex.replace(/[^0-9a-fA-F#]/g, ''); // Remove invalid characters
      setHexInput(newHex);

      // Only try to update color if we have a complete hex code
      if (newHex.length >= 3) {
        const hexWithHash = newHex.startsWith('#') ? newHex : `#${newHex}`;

        // Check if it's a valid hex color (3 or 6 digits)
        if (HEX_COLOR_REGEX.test(hexWithHash)) {
          try {
            const rgb = hexToRgb(hexWithHash);
            if (rgb) {
              const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
              setColor({
                hex: hexWithHash,
                hsv: { a: 1, h: hsv.h, s: hsv.s, v: hsv.v },
                rgb: { a: 1, b: rgb.b, g: rgb.g, r: rgb.r },
              });
            }
          } catch (error) {
            reportErrorToBugsnag({
              context: 'Failed to set color from hex input',
              error,
              metadata: {
                hexInput: newHex,
                hexWithHash,
              },
            });
          }
        }
      }
    },
    [setColor],
  );

  const handleColorPalettePickerChange = useCallback(
    (color: IColor) => {
      setColor(sanitizeColorValues(color));
      setTouched(true);
    },
    [setColor],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTouched(true);
      handleHexChange(event.target.value);
    },
    [handleHexChange],
  );

  const handleClear = useCallback(() => {
    setTouched(false);
    handleHexChange(defaultColor);
  }, [defaultColor, handleHexChange]);

  // Update input when color changes from picker
  // biome-ignore lint/correctness/useExhaustiveDependencies(onChange): onChange is intentionally excluded to prevent potential infinite loops when parent component passes unstable onChange callbacks
  useEffect(() => {
    setHexInput(color.hex);
    onChange?.(color.hex);
  }, [color]);

  // Check if the current hex input is valid
  const isValidHex = useMemo(() => {
    if (!hexInput) return false;
    const hexWithHash = hexInput.startsWith('#') ? hexInput : `#${hexInput}`;
    return HEX_COLOR_REGEX.test(hexWithHash);
  }, [hexInput]);

  return (
    <div className="color-picker-container flex flex-col gap-2">
      <div className="flex w-full items-center gap-2">
        <div className="relative w-full min-w-0 flex-1">
          <Input
            className="w-full uppercase"
            data-testid="CUSTOM_COLOR_HEX_INPUT"
            onChange={handleInputChange}
            placeholder="#000000"
            type="text"
            value={hexInput}
          />
          {showClearButton && (
            <button
              className="absolute top-1/2 right-2 -translate-y-1/2 p-1"
              data-testid="CUSTOM_COLOR_CLEAR_BUTTON"
              onClick={handleClear}
              type="button"
            >
              <CloseIcon className="size-3" />
            </button>
          )}
        </div>
        <Button
          appearance="filled"
          color="grey"
          data-testid="CUSTOM_COLOR_SAVE_BUTTON"
          disabled={!isValidHex}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
      <ColorPalettePicker
        color={color}
        data-testid="COLOR_PICKER"
        hideAlpha
        hideInput={HIDDEN_INPUT_TYPES}
        onChange={handleColorPalettePickerChange}
      />
    </div>
  );
});

CustomColorPickerInput.displayName = 'CustomColorPickerInput';
